import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { setSubscription } from '../../../Redux/sessionSlice';
import { loadStudents } from '../../../Redux/studentsSlice';
import { loadLessons } from '../../../Redux/lessonsSlice';
import { SubscriptionService } from '../../../Services/SubscriptionService';
import { AppHeader } from '../../LayoutArea/AppHeader/AppHeader';
import { Login } from '../Login/Login';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './AuthGate.css';

/**
 * The guard on everything under /user, and the shell those screens sit in.
 *
 * Three states, not two. Until Firebase has restored the stored session the
 * answer is "not known yet", and showing the login screen during that moment
 * makes the app flash sign-in on every single reload. The session itself is
 * subscribed to in AppLayout, above every route, so the marketing home can
 * recognise a returning tutor as well.
 *
 * This is a convenience, not a permission: the API verifies the token on every
 * request, so getting past this component grants nothing on its own.
 */
export function AuthGate() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(state => state.session.status);

  useEffect(() => {
    if (status !== 'signed-in') return;
    // The roster and the lesson list are loaded once, here, because almost
    // every screen needs both: a lesson's debt state depends on its student's
    // recurring slots, so neither is useful without the other.
    void dispatch(loadStudents());
    void dispatch(loadLessons());
    // Entitlement decides which screens are offered. A failure is not fatal:
    // the user stays on the free view and the server still refuses anything
    // they are not entitled to.
    SubscriptionService.get()
      .then(subscription => dispatch(setSubscription(subscription)))
      .catch(() => dispatch(setSubscription(null)));
  }, [status, dispatch]);

  if (status === 'unknown') {
    return <div className="auth-gate"><Spinner /></div>;
  }
  if (status === 'signed-out') {
    return <Login />;
  }

  return (
    <div className="app-shell">
      <AppHeader />
      <main className="app-shell-content">
        <Outlet />
      </main>
    </div>
  );
}
